import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { padLeadingZero } from './math';

dayjs.extend(utc);
dayjs.extend(weekOfYear);

export const getDayjs = () => dayjs;

export const formatDate = (date: Date | number, tpl = 'YYYY-MM-DD HH:mm:ss') => {
  if (typeof date === 'number') {
    return dayjs(new Date(date)).format(tpl);
  }

  return dayjs(date).format(tpl);
};

export const getWeek = () => {
  const yearStarDay = dayjs().startOf('year');
  const dayWeek = dayjs(yearStarDay).day();
  if (dayWeek === 0) {
    return dayjs().week() - 1;
  }
  return dayjs().week();
};

export const getMonth = (date: Date) => dayjs(date).month() + 1;

export const getDay = (date: Date) => dayjs(date).day();

export const getDate = (date: Date) => dayjs(date).date();

export const getWeeksInYear = (year: number) => {
  const lastDayOfYear = dayjs().year(year).endOf('year');
  return lastDayOfYear.week() === 1 ? lastDayOfYear.subtract(1, 'week').week() : lastDayOfYear.week();
};

/**
 * date: 后端返回的日期数据，均默认+8时区，转换为当前客户端时区的时间戳
 */
export const getTimeStamp = (date: string) => {
  // 将+8时区的时间字符串转为UTC
  const utcTime = dayjs(date).utcOffset(480, true).utc();

  // 转换到客户端本地时区
  const localTime = dayjs(utcTime).local();

  // 获取本地时间戳
  return localTime.valueOf();
};

/**
 * date: 后端返回的数据，返回dayjs
 */
export const getTimeDate = (date: string | number) => {
  // 时间戳
  if (/^[0-9]*$/.test(`${date}`)) {
    return `${date}`.length === 10 ? dayjs.unix(Number(date)) : dayjs(Number(date));
  }
  return dayjs(getTimeStamp(`${date}`));
};

/**
 * date: 后端返回的日期数据，均默认+8时区，转换为当前客户端时区的时间戳
 */
export const getTimeDiffText = (date: string | number): string => {
  const currentTime = dayjs().valueOf();
  const rewardTime = getTimeDate(date).valueOf();
  const timeDiff = currentTime - rewardTime;
  const minuteDiff = Math.floor(timeDiff / (1000 * 60));
  const hoursDiff = Math.floor(timeDiff / (1000 * 60 * 60));
  const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  if (minuteDiff < 1) {
    return `${'date_just_now'}`;
  } else if (hoursDiff < 1) {
    return `${'date_minute_age'}`;
  } else if (daysDiff < 1) {
    return `${'date_hours_age'}`;
  } else if (daysDiff < 30) {
    return `${'date_days_age'}`;
  } else {
    return getTimeDate(date).format('MM-DD');
  }
};
export const formatTimestamp = (timestamp: string | number, year = false) => {
  timestamp = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
  const date = dayjs.unix(timestamp);
  if (year) return date.format('DD-MM-YYYY HH:mm:ss');
  return date.format('DD/MM, HH:mm:ss');
};

export const formatDateTime = (dateTimeStr: string): string => {
  const dateTime = dayjs(dateTimeStr);
  return dateTime.format('MM-DD HH:mm:ss');
};
/**
 * ====================================================================================
 */

export const dateFormat = (t, style = 1) => {
  let d = new Date();
  d.setTime(t);
  if (style === 2) {
    return `${d.getFullYear()}/${padLeadingZero(d.getMonth() + 1)}/${padLeadingZero(d.getDate())}`;
  } else if (style === 21) {
    // style === 2 的变种，只是改 / 为 -
    return `${d.getFullYear()}-${padLeadingZero(d.getMonth() + 1)}-${padLeadingZero(d.getDate())}`;
  } else if (style === 3) {
    return `${d.getFullYear()}-${padLeadingZero(d.getMonth() + 1)}`;
  } else if (style === 4) {
    return `${d.getFullYear()}-${padLeadingZero(d.getMonth() + 1)}-${padLeadingZero(d.getDate())} ${padLeadingZero(d.getHours())}:${padLeadingZero(d.getMinutes())}`;
  } else if (style === 5) {
    return `${d.getFullYear()}/${padLeadingZero(d.getMonth() + 1)}/${padLeadingZero(d.getDate())} ${padLeadingZero(d.getHours())}:${padLeadingZero(d.getMinutes())}:${padLeadingZero(d.getSeconds())}`;
  } else if (style === 6) {
    return `${padLeadingZero(d.getHours())}:${padLeadingZero(d.getMinutes())}`;
  }
  return `${d.getFullYear()}-${padLeadingZero(d.getMonth() + 1)}-${padLeadingZero(d.getDate())} ${padLeadingZero(d.getHours())}:${padLeadingZero(d.getMinutes())}:${padLeadingZero(d.getSeconds())}`;
};

// 输入时间对象 返回 年/月/日 perxp 为分隔符 默认 '/'
export const getYMD = (date: Date, perxp: string = '/') => {
  const year = date.getFullYear();
  let _month: string | number = date.getMonth() + 1;
  if (_month < 10) {
    _month = '0' + _month;
  }
  let _day: string | number = date.getDate();
  if (_day < 10) {
    _day = '0' + _day;
  }
  return year + perxp + _month + perxp + _day;
};

/**
 * 经过验证的新的开始时间计算方式
 */
export const dayStartTimeNew = (time: any) => {
  if (!time) {
    return '';
  }
  if (/^\d+$/.test(time)) {
    // 传入的为时间戳
    time = getYMD(new Date(time));
  } else if (time instanceof Date) {
    // 传入的为时间对象
    time = getYMD(time);
  }
  // time 需为 yyyy-MM-dd 或 yyyy/MM/dd 的年月日时间格式
  return new Date(`${time.replace(/-/g, '/')} 00:00:00`).getTime();
};
/**
 * 经过验证的新的结束时间计算方式
 */
export const dayEndTimeNew = (time: any) => {
  if (!time) {
    return '';
  }
  if (/^\d+$/.test(time)) {
    // 传入的为时间戳
    time = getYMD(new Date(time));
  } else if (time instanceof Date) {
    // 传入的为时间对象
    time = getYMD(time);
  }
  // time 需为 yyyy-MM-dd 或 yyyy/MM/dd 的年月日时间格式
  return new Date(`${time.replace(/-/g, '/')} 23:59:59`).getTime();
};

export const stopTime = term => {
  let delayTime = Number(term.end_time) - Number(term.stop_sale_offset) * 1000 - Number(term.db_sys_date); //  服务器时间多久后结束
  return new Date().getTime() + delayTime;
};
